/**
 * Dealer 详情页 (SUPER_ADMIN) —— /dealers/:id
 * 5 Tab：基本信息 / 管理员账号 / Coupons / PromotionCodes / 月报
 */
import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconBuildingStore, IconCoin, IconFileSpreadsheet, IconUsers } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  addDealerAdminUser,
  createCouponSuper,
  deactivateCouponSuper,
  deactivateDealer,
  deactivatePromoSuper,
  exportMonthlyReportSuperUrl,
  getDealer,
  getMonthlyReportSuper,
  listCouponsSuper,
  listDealerAdminUsers,
  removeDealerAdminUser,
  searchUsersForDealer,
  updateDealer,
  type CouponCreateRequest,
  type CouponDetail,
  type DealerDetail,
  type MonthlyReportResponse,
  type ReportQuery,
  type SearchableUser,
  type UserBrief,
} from '@mygpt/app/apis/services/dealer';
import { Http } from '@mygpt/app/http';

import { useGPTStore } from '@/store';

import { CouponCreateModal } from './components/CouponCreateModal';
import { PromoCardItem } from './components/PromoCardItem';
import { QRCodeCard } from './components/QRCodeCard';

const hasConfig = (userInfo: any, key: string): boolean => {
  if (!userInfo?.userconfigs) return false;
  return userInfo.userconfigs.some((c: any) => c.key === key && String(c.value).toLowerCase() === 'true');
};

const DealerDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userInfo } = useGPTStore((state) => state);

  // 权限：详情页仅 SUPER_ADMIN 可见
  useEffect(() => {
    if (!userInfo) return;
    const isSuperAdmin = hasConfig(userInfo, 'super_admin');
    const isDealerAdmin = hasConfig(userInfo, 'dealer_admin');
    if (!isSuperAdmin && isDealerAdmin) {
      navigate('/dealers/me', { replace: true });
    } else if (!isSuperAdmin) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const [dealer, setDealer] = useState<DealerDetail | null>(null);
  const [users, setUsers] = useState<UserBrief[]>([]);
  const [coupons, setCoupons] = useState<CouponDetail[]>([]);
  const [report, setReport] = useState<MonthlyReportResponse | null>(null);

  const [createCouponOpen, setCreateCouponOpen] = useState(false);
  const [issuedCoupon, setIssuedCoupon] = useState<CouponDetail | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  // 添加管理员 Modal：选既有用户
  const [addUserQuery, setAddUserQuery] = useState('');
  const [debouncedAddUserQuery] = useDebouncedValue(addUserQuery, 300);
  const [addUserOptions, setAddUserOptions] = useState<SearchableUser[]>([]);
  const [addUserSelectedId, setAddUserSelectedId] = useState<string | null>(null);
  const [addingUser, setAddingUser] = useState(false);

  // v0.9: 查询窗口统一用日期范围，默认当月 1 日~当月末
  const [filterEmail, setFilterEmail] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [dateRange, setDateRange] = useState<DatesRangeValue>(() => [
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD'),
  ]);

  const reportParams = (): ReportQuery => {
    const p: ReportQuery = {};
    if (dateRange[0] && dateRange[1]) {
      p.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
      p.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
    }
    if (filterEmail.trim()) p.customer_email = filterEmail.trim();
    if (filterCode.trim()) p.code = filterCode.trim();
    return p;
  };

  const fetchDealer = async () => {
    if (!id) return;
    const res = await getDealer(id);
    setDealer(res.data);
  };
  const fetchUsers = async () => {
    if (!id) return;
    const res = await listDealerAdminUsers(id);
    setUsers(res.data);
  };
  const fetchCoupons = async () => {
    if (!id) return;
    const res = await listCouponsSuper(id, { page: 1, page_size: 100 });
    setCoupons(res.data.items);
  };
  const fetchReport = async () => {
    if (!id) return;
    const res = await getMonthlyReportSuper(id, { ...reportParams(), include_audit: true });
    setReport(res.data);
  };

  useEffect(() => {
    fetchDealer();
    fetchUsers();
    fetchCoupons();
  }, [id]);

  // 添加管理员 Modal 打开 + 搜索：拉用户列表
  useEffect(() => {
    if (!addUserOpen) return;
    searchUsersForDealer({ q: debouncedAddUserQuery.trim() || undefined, limit: 20 })
      .then((res) => setAddUserOptions(res.data))
      .catch(() => setAddUserOptions([]));
  }, [addUserOpen, debouncedAddUserQuery]);

  const addUserSelectData = useMemo(
    () =>
      addUserOptions.map((u) => ({
        value: u.id,
        label: `${u.email} (${u.name})${u.dealer_id ? ' · ' + t('dealer.user_already_in_dealer') : ''}`,
        disabled: !!u.dealer_id,
      })),
    [addUserOptions, t]
  );

  const handleAddUser = async () => {
    if (!id || !addUserSelectedId) return;
    setAddingUser(true);
    try {
      await addDealerAdminUser(id, { user_id: addUserSelectedId });
      notifications.show({ title: t('dealer.add_admin'), message: '', color: 'green' });
      setAddUserOpen(false);
      setAddUserQuery('');
      setAddUserSelectedId(null);
      setAddUserOptions([]);
      fetchUsers();
    } catch (e: any) {
      notifications.show({ color: 'red', message: e?.response?.data?.detail || String(e) });
    } finally {
      setAddingUser(false);
    }
  };

  if (!dealer) {
    return (
      <Container size="lg" py="xl">
        <Text c="dimmed">{t('dealer.loading')}</Text>
      </Container>
    );
  }

  const submitCoupon = async (req: CouponCreateRequest) => {
    if (!id) return;
    try {
      const res = await createCouponSuper(id, req);
      setIssuedCoupon(res.data);
      setCreateCouponOpen(false);
      await fetchCoupons();
      notifications.show({ title: t('coupon.issued'), message: res.data.name, color: 'green' });
    } catch (e: any) {
      notifications.show({
        title: 'Error',
        message: e?.response?.data?.detail || String(e),
        color: 'red',
      });
    }
  };

  const downloadReport = async (format: 'xlsx' | 'csv') => {
    if (!id) return;
    const url = exportMonthlyReportSuperUrl(id, { ...reportParams(), format, include_audit: true });
    const http = Http.use();
    const resp = await http.get(url, { responseType: 'blob' });
    const blob = new Blob([resp.data]);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const stamp = dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM') : dayjs().format('YYYY-MM');
    a.download = `incentive_report_${dealer.code}_${stamp}.${format}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Container size="lg" py="xl">
      <Group mb="md">
        <Anchor onClick={() => navigate('/dealers')}>← {t('dealer.list_title')}</Anchor>
      </Group>
      <Group mb="lg" justify="space-between">
        <Title order={2}>
          {dealer.code} / {dealer.name}
        </Title>
        <Badge color={dealer.is_active ? 'green' : 'gray'} variant="light" size="lg">
          {dealer.is_active ? t('dealer.active') : t('dealer.inactive')}
        </Badge>
      </Group>

      <Tabs defaultValue="info">
        <Tabs.List>
          <Tabs.Tab value="info" leftSection={<IconBuildingStore size={16} />}>
            {t('dealer.tab_info')}
          </Tabs.Tab>
          <Tabs.Tab value="users" leftSection={<IconUsers size={16} />}>
            {t('dealer.tab_admins')}
          </Tabs.Tab>
          <Tabs.Tab value="coupons" leftSection={<IconCoin size={16} />}>
            {t('coupon.list_title')}
          </Tabs.Tab>
          <Tabs.Tab value="report" leftSection={<IconFileSpreadsheet size={16} />}>
            {t('dealer_portal.report.title')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="info" pt="md">
          <Card withBorder>
            <Stack>
              <Group grow>
                <TextInput
                  label={t('dealer.contact_email')}
                  value={dealer.contact_email}
                  onChange={(e) => setDealer({ ...dealer, contact_email: e.currentTarget.value })}
                />
                <TextInput
                  label={t('dealer.contact_phone')}
                  value={dealer.contact_phone || ''}
                  onChange={(e) => setDealer({ ...dealer, contact_phone: e.currentTarget.value })}
                />
              </Group>
              <NumberInput
                label={t('dealer.incentive_rate')}
                decimalScale={4}
                step={0.05}
                value={Number(dealer.incentive_rate)}
                onChange={(v) => setDealer({ ...dealer, incentive_rate: String(v) })}
              />
              <Group>
                <Button
                  onClick={async () => {
                    await updateDealer(dealer.id, {
                      contact_email: dealer.contact_email,
                      contact_phone: dealer.contact_phone,
                      incentive_rate: dealer.incentive_rate,
                    });
                    notifications.show({ title: t('dealer.saved'), message: '', color: 'green' });
                  }}
                >
                  {t('dealer.save')}
                </Button>
                {dealer.is_active ? (
                  <Button
                    color="red"
                    variant="light"
                    onClick={async () => {
                      await deactivateDealer(dealer.id);
                      notifications.show({ title: t('dealer.deactivated'), message: '', color: 'gray' });
                      fetchDealer();
                    }}
                  >
                    {t('dealer.deactivate')}
                  </Button>
                ) : (
                  <Button
                    color="green"
                    variant="light"
                    onClick={async () => {
                      await updateDealer(dealer.id, { is_active: true });
                      notifications.show({ title: t('dealer.activated'), message: '', color: 'green' });
                      fetchDealer();
                    }}
                  >
                    {t('dealer.activate')}
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="users" pt="md">
          <Group mb="md">
            <Button onClick={() => setAddUserOpen(true)}>{t('dealer.add_admin')}</Button>
          </Group>
          <Card withBorder p={0}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('dealer.admin_email')}</Table.Th>
                  <Table.Th>{t('dealer.admin_name')}</Table.Th>
                  <Table.Th>{t('dealer.admin_action')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((u) => (
                  <Table.Tr key={u.id}>
                    <Table.Td>{u.email}</Table.Td>
                    <Table.Td>{u.name}</Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={async () => {
                          if (!id) return;
                          await removeDealerAdminUser(id, u.id);
                          fetchUsers();
                        }}
                      >
                        {t('dealer.admin_remove')}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="coupons" pt="md">
          <Group mb="md">
            <Button
              onClick={() => setCreateCouponOpen(true)}
              disabled={!dealer?.is_active}
              title={!dealer?.is_active ? (t('dealer.dealer_inactive_hint') as string) : undefined}
            >
              + {t('coupon.issue')}
            </Button>
            {!dealer?.is_active && (
              <Text size="sm" c="dimmed">
                {t('dealer.dealer_inactive_hint')}
              </Text>
            )}
          </Group>
          <Stack>
            {coupons.map((c) => (
              <Card key={c.id} withBorder>
                <Stack>
                  <Group justify="space-between">
                    <Box>
                      <Group gap="xs">
                        <Text fw={600}>{c.name}</Text>
                        <Badge color={c.is_active ? 'green' : 'gray'}>
                          {c.is_active ? t('dealer.active') : t('dealer.inactive')}
                        </Badge>
                        <Badge variant="light">
                          {c.issued_by_role === 'super_admin'
                            ? t('dealer.role_super_admin')
                            : t('dealer.role_dealer_admin')}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {c.percent_off ? `${c.percent_off}%` : `¥${c.amount_off}`} ·{' '}
                        {t(`coupon.duration.${c.duration}`)}
                      </Text>
                    </Box>
                    {/* Coupon 级停用入口隐藏 —— 业务上 1 Coupon = 1 PromotionCode，
                        在下方 PromoCardItem 里停用单个码即可，无需整体停 Coupon */}
                  </Group>

                  {c.promotion_codes.length > 0 && (
                    <Stack gap="xs">
                      {c.promotion_codes.map((p) => (
                        <PromoCardItem
                          key={p.id}
                          promo={p}
                          onDeactivate={async (promoId) => {
                            await deactivatePromoSuper(promoId);
                            fetchCoupons();
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="report" pt="md">
          <Group mb="md" align="flex-end" wrap="wrap">
            <DatePickerInput
              type="range"
              label={t('dealer.report_date_range')}
              placeholder={t('dealer.report_date_range_placeholder')}
              valueFormat="YYYY-MM-DD"
              value={dateRange}
              onChange={setDateRange}
              clearable
              w={260}
            />
            <TextInput
              label={t('dealer.report_customer_email')}
              placeholder={t('dealer.report_filter_email_placeholder')}
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.currentTarget.value)}
              w={220}
            />
            <TextInput
              label={t('dealer.report_code')}
              placeholder={t('dealer.report_filter_code_placeholder')}
              value={filterCode}
              onChange={(e) => setFilterCode(e.currentTarget.value)}
              w={180}
            />
            <Button onClick={fetchReport}>{t('dealer.query')}</Button>
            <Button variant="light" onClick={() => downloadReport('xlsx')}>
              {t('dealer_portal.report.download_xlsx')}
            </Button>
          </Group>
          {report && (
            <Card withBorder p={0}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('dealer.report_customer_email')}</Table.Th>
                    <Table.Th>{t('dealer.report_plan')}</Table.Th>
                    <Table.Th>{t('dealer.report_code')}</Table.Th>
                    <Table.Th>{t('dealer.report_contract_date')}</Table.Th>
                    <Table.Th>{t('dealer.report_status')}</Table.Th>
                    <Table.Th>{t('dealer.report_standard')}</Table.Th>
                    <Table.Th>{t('dealer.report_discount')}</Table.Th>
                    <Table.Th>{t('dealer.report_net')}</Table.Th>
                    <Table.Th>{t('dealer.report_incentive')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {report.rows.map((r, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{r.customer_email}</Table.Td>
                      <Table.Td>{r.plan_name}</Table.Td>
                      <Table.Td>{r.coupon_code}</Table.Td>
                      <Table.Td>
                        {new Date(r.contract_date).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={r.status === 'trial' ? 'gray' : 'green'} variant="light">
                          {r.status === 'trial' ? t('dealer.report_status_trial') : t('dealer.report_status_paid')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>¥{r.standard_amount.toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>¥{r.discount_amount.toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>¥{r.net_amount.toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>¥{r.dealer_incentive_amount.toLocaleString('ja-JP')}</Table.Td>
                    </Table.Tr>
                  ))}
                  {report.rows.length > 0 && (
                    <Table.Tr fw={600} bg="gray.0">
                      <Table.Td colSpan={5}>{t('dealer.report_totals')}</Table.Td>
                      <Table.Td>
                        ¥{report.rows.reduce((s, r) => s + r.standard_amount, 0).toLocaleString('ja-JP')}
                      </Table.Td>
                      <Table.Td>
                        ¥{report.rows.reduce((s, r) => s + r.discount_amount, 0).toLocaleString('ja-JP')}
                      </Table.Td>
                      <Table.Td>¥{report.rows.reduce((s, r) => s + r.net_amount, 0).toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>
                        ¥{report.rows.reduce((s, r) => s + r.dealer_incentive_amount, 0).toLocaleString('ja-JP')}
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Tabs.Panel>
      </Tabs>

      <CouponCreateModal opened={createCouponOpen} onClose={() => setCreateCouponOpen(false)} onSubmit={submitCoupon} />

      <Modal
        opened={!!issuedCoupon}
        onClose={() => setIssuedCoupon(null)}
        title={t('coupon.issued_modal_title')}
        size="lg"
      >
        {issuedCoupon && issuedCoupon.promotion_codes[0] && (
          <Stack align="center">
            <QRCodeCard code={issuedCoupon.promotion_codes[0].code} url={issuedCoupon.promotion_codes[0].qr_code_url} />
            <Button onClick={() => setIssuedCoupon(null)}>{t('coupon.back_to_list')}</Button>
          </Stack>
        )}
      </Modal>

      <Modal opened={addUserOpen} onClose={() => setAddUserOpen(false)} title={t('dealer.add_admin')} size="md">
        <Stack>
          <Select
            label={t('dealer.search_admin_user')}
            placeholder={t('dealer.search_admin_user_placeholder') as string}
            data={addUserSelectData}
            value={addUserSelectedId}
            onChange={(v) => setAddUserSelectedId(v)}
            searchable
            searchValue={addUserQuery}
            onSearchChange={setAddUserQuery}
            nothingFoundMessage={t('dealer.no_dealers') as string}
            clearable
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAddUserOpen(false)}>
              {t('coupon.cancel')}
            </Button>
            <Button onClick={handleAddUser} disabled={!addUserSelectedId} loading={addingUser}>
              {t('dealer.add_admin')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default DealerDetailPage;
